/**
 * Sincronização de edição entre abas do MESMO relatório (#21909), via
 * `BroadcastChannel` — API nativa do browser, sem dependência e sem backend.
 *
 * Quando uma aba salva com sucesso, ela anuncia `{ type: "saved", reportId,
 * modifiedAt }` no canal daquele relatório. As outras abas abertas no mesmo
 * relatório escutam e re-hidratam seu estado a partir do servidor (ou, se têm
 * edição pendente própria, deixam o fluxo de conflito 409 reconciliar). Assim
 * as abas convergem em tempo real e o 409 vira exceção rara em vez de regra.
 *
 * Escopo: abas do mesmo navegador/perfil. Navegadores/máquinas distintos não
 * compartilham o canal — nesse caso o guard de concorrência 409 no servidor
 * continua protegendo contra perda de dados (degradação segura).
 */

export interface ReportSavedMessage {
  type: "saved";
  reportId: string;
  modifiedAt?: string;
  /** Identificador da aba que originou o save, para ignorar o próprio eco. */
  origin: string;
}

const CHANNEL_PREFIX = "report-sync:";

/** Id estável desta aba durante a vida da página. */
export const TAB_ID =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `tab-${Math.random().toString(36).slice(2)}-${Date.now()}`;

const isSupported = (): boolean =>
  typeof window !== "undefined" && typeof window.BroadcastChannel !== "undefined";

/**
 * Abre o canal de um relatório. Retorna `null` quando `BroadcastChannel` não
 * existe (ambiente sem suporte / testes sem polyfill) — os chamadores devem
 * tratar `null` como "sync indisponível", sem quebrar o fluxo de save.
 */
export function openReportSyncChannel(reportId: string): BroadcastChannel | null {
  if (!isSupported() || !reportId) return null;
  try {
    return new BroadcastChannel(`${CHANNEL_PREFIX}${reportId}`);
  } catch {
    return null;
  }
}

/** Anuncia que esta aba salvou o relatório. No-op se o canal for nulo. */
export function broadcastReportSaved(
  channel: BroadcastChannel | null,
  reportId: string,
  modifiedAt?: string,
): void {
  if (!channel) return;
  try {
    const message: ReportSavedMessage = {
      type: "saved",
      reportId,
      modifiedAt,
      origin: TAB_ID,
    };
    channel.postMessage(message);
  } catch {
    // Canal fechando/fechado durante unmount: silencioso, não é fatal.
  }
}

/**
 * Assina mensagens `saved` de OUTRAS abas (ignora o próprio eco por `origin`).
 * Retorna uma função de cleanup que remove o listener.
 */
export function subscribeReportSaved(
  channel: BroadcastChannel | null,
  onSaved: (message: ReportSavedMessage) => void,
): () => void {
  if (!channel) return () => {};
  const handler = (event: MessageEvent) => {
    const data = event?.data as ReportSavedMessage | undefined;
    if (!data || data.type !== "saved") return;
    if (data.origin === TAB_ID) return; // próprio eco
    onSaved(data);
  };
  channel.addEventListener("message", handler);
  return () => {
    try {
      channel.removeEventListener("message", handler);
    } catch {
      /* canal já fechado */
    }
  };
}
